import { useDispatch } from 'react-redux'
import { Button, ButtonProps, Dialog, DialogContent, DialogTitle } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { hideSector, showSector } from '../redux/slices/sectors'
import { useTranslation } from 'react-i18next'
import { Project } from '../types'
import { useState } from 'react'
import SectorForm from './SectorForm'
import { usePolicy } from '../hooks'
import { ProjectStateType } from '../enums/ProjectStateType'

export interface SectorCreateButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  project?: Project
}

export default function SectorCreateButton({ project, ...props }: SectorCreateButtonProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * Indicates if the dialog is open.
   */
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  /**
   * Open the dialog.
   */
  function openDialog(): void {
    dispatch(showSector(undefined))
    setIsDialogOpen(true)
  }

  /**
   * Close the dialog.
   */
  function closeDialog(): void {
    setIsDialogOpen(false)
    dispatch(hideSector())
  }

  return (
    <>
      {project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project) && (
        <Button
          variant="contained"
          color="primary"
          endIcon={<AddIcon fontSize="inherit" />}
          {...props}
          onClick={() => openDialog()}
        >
          {t('newSector')}
        </Button>
      )}

      <Dialog open={isDialogOpen} maxWidth="sm" fullWidth onClose={() => closeDialog()}>
        <DialogTitle>{t('newSector')}</DialogTitle>
        <DialogContent>
          <SectorForm
            value={{ project: project?.id }}
            onCancel={() => closeDialog()}
            afterSubmit={() => closeDialog()}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
